

"use client"
import { useState } from "react"
import { Table, Badge } from "react-bootstrap"
import { Mail, Phone, User } from "lucide-react"
import ContactPopupView from "../../../contacts/contactpopup/ContactPopupView"


const HRContactsTab = ({ organization, contacts, users, onUpdateContact }) => {
  const [selectedContactForPopup, setSelectedContactForPopup] = useState(null)
  const [showContactPopup, setShowContactPopup] = useState(false)
  const [defaultActiveTab, setDefaultActiveTab] = useState("summary")

  // Get all contacts for this organization
  const getOrganizationContacts = () => {
    // Start with contacts from organization.contactPersonIds if they are objects
    const directContacts = Array.isArray(organization?.contactPersonIds) 
      ? organization.contactPersonIds.filter(cp => typeof cp === 'object' && cp.id)
      : [];

    // Also get contacts from the contacts prop that belong to this organization
    const propContacts = contacts.filter(
      (contact) => contact.organizationId === organization.id || contact.organizationId === "Not Available",
    );

    // Merge and remove duplicates by ID
    const merged = [...directContacts];
    propContacts.forEach(pc => {
      if (!merged.some(m => m.id === pc.id)) {
        merged.push(pc);
      }
    });

    return merged;
  };

  // Get assigned Placement Officer details by assignedToId - returns NA if not found
  const getAssignedOfficer = (assignedToId) => {
    if (!assignedToId) return null;

    // Handle array of objects (enriched from backend)
    if (Array.isArray(assignedToId)) {
      return assignedToId.length > 0 ? assignedToId[0] : null;
    }

    // Handle single object
    if (typeof assignedToId === 'object') {
      return assignedToId;
    }

    // Handle string ID (lookup in users prop)
    if (typeof assignedToId === 'string' && users?.length) {
      const user = users.find((user) => user.id === assignedToId);
      return user || null;
    }

    return null;
  }

  const handleViewContact = (contactId) => {
    const allContacts = getOrganizationContacts();
    const contact = allContacts.find((c) => c.id === contactId)
    if (contact) {
      setSelectedContactForPopup(contact)
      setDefaultActiveTab("summary")
      setShowContactPopup(true)
    }
  }

  const handleUpdateContactCallback = (updatedContact) => {
    setSelectedContactForPopup(updatedContact)
    if (onUpdateContact) {
      onUpdateContact(updatedContact)
    }
  }

  const handleCloseContactPopup = () => {
    setShowContactPopup(false)
    setSelectedContactForPopup(null)
  }

  const organizationContacts = getOrganizationContacts()

  return (
    <div className="hr-contacts-content">
      <div className="hr-contacts-header" style={{ marginBottom: "20px" }}>
        <h6 style={{ fontSize: "14px", fontWeight: "600", color: "#495057" }}>HR Contacts for {organization?.name}</h6>
        <p style={{ fontSize: "12px", color: "#6c757d", marginBottom: 0 }}>
          Total Contacts: {organizationContacts.length}
        </p>
      </div>

      {organizationContacts.length > 0 ? (
        <div className="table-responsive">
          <Table striped bordered hover size="sm" style={{ fontSize: "13px", marginBottom: 0 }}>
            <thead style={{ backgroundColor: "#f8f9fa" }}>
              <tr>
                <th style={{ width: "25%", fontWeight: "600" }}>Contact Name</th>
                <th style={{ width: "20%", fontWeight: "600" }}>Email</th>
                <th style={{ width: "15%", fontWeight: "600" }}>Phone</th>
                <th style={{ width: "25%", fontWeight: "600" }}>Assigned Placement Officer</th>
                <th style={{ width: "15%", fontWeight: "600" }}>Placement Officer Email</th>
              </tr>
            </thead>
            <tbody>
              {organizationContacts.map((contact) => {
                const assignedOfficer = getAssignedOfficer(contact.assignedToId)

                return (
                  <tr key={contact.id}>
                    <td
                      style={{ verticalAlign: "middle", cursor: "pointer" }}
                      onClick={() => handleViewContact(contact.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && handleViewContact(contact.id)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "#0d6efd",
                            color: "white",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "600",
                            flexShrink: 0,
                          }}
                        >
                          {contact.name
                            ?.split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .substring(0, 2)}
                        </div>
                        <span
                          style={{
                            color: "#0d6efd",
                            cursor: "pointer",
                            fontWeight: "500",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}

                        >
                          {contact.name}
                        </span>
                      </div>
                    </td>

                    <td style={{ verticalAlign: "middle" }}>
                      <a
                        href={`mailto:${contact.email}`}
                        style={{
                          color: "#5E5E5E",
                          textDecoration: "none",
                          fontSize: "12px",
                        }}
                      >
                        <Mail size={14} style={{ marginRight: "4px", display: "inline" }} />
                        {contact.email}
                      </a>
                    </td>

                    <td style={{ verticalAlign: "middle" }}>
                      {contact.phone ? (
                        <a
                          href={`tel:${contact.phone}`}
                          style={{
                            color: "#5E5E5E",
                            textDecoration: "none",
                            fontSize: "12px",
                          }}
                        >
                          <Phone size={14} style={{ marginRight: "4px", display: "inline" }} />
                          {contact.phone}
                        </a>
                      ) : (
                        <span style={{ color: "#6c757d", fontSize: "12px" }}>Not provided</span>
                      )}
                    </td>

                    <td style={{ verticalAlign: "middle" }}>
                      {assignedOfficer ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "50%",
                              backgroundColor: "#0d6efd",
                              color: "white",
                              fontSize: "11px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "600",
                              flexShrink: 0,
                            }}
                          >
                            {`${assignedOfficer.firstName?.substring(0, 1) || ""}${assignedOfficer.lastName?.substring(0, 1) || ""}`.toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: "500", color: "#495057", fontSize: "12px" }}>
                              {`${assignedOfficer.firstName || ""} ${assignedOfficer.lastName || ""}`.trim()}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <Badge bg="secondary" style={{ fontSize: "11px" }}>
                          NA
                        </Badge>
                      )}
                    </td>

                    <td style={{ verticalAlign: "middle" }}>
                      {assignedOfficer?.email || assignedOfficer?.emailId ? (
                        <a
                          href={`mailto:${assignedOfficer.email || assignedOfficer.emailId}`}
                          style={{
                            color: "#5E5E5E",
                            textDecoration: "none",
                            fontSize: "12px",
                          }}
                        >
                          {assignedOfficer.email || assignedOfficer.emailId}
                        </a>
                      ) : (
                        <span style={{ color: "#6c757d", fontSize: "12px" }}>NA</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      ) : (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            backgroundColor: "#f8f9fa",
            borderRadius: "6px",
            border: "1px solid #dee2e6",
          }}
        >
          <User size={32} style={{ color: "#6c757d", marginBottom: "12px" }} />
          <p style={{ color: "#6c757d", fontSize: "14px", marginBottom: 0 }}>
            No HR contacts found for this organization
          </p>
        </div>
      )}

      {selectedContactForPopup && (
        <ContactPopupView
          show={showContactPopup}
          onHide={handleCloseContactPopup}
          contact={selectedContactForPopup}
          onUpdateContact={handleUpdateContactCallback}
          defaultActiveTab={defaultActiveTab}
        />
      )}

      <style>{`
        .hr-contacts-content {
          padding: 20px;
          background: white;
        }

        .hr-contacts-header {
          border-bottom: 2px solid #f0f0f0;
          padding-bottom: 12px;
        }

        .table-responsive {
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #dee2e6;
        }

        .table {
          margin-bottom: 0;
        }

        .table thead th {
          background-color: #f8f9fa;
          border-bottom: 2px solid #dee2e6;
          color: #495057;
          padding: 12px 8px;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table tbody td {
          padding: 12px 8px;
          border-color: #dee2e6;
        }

        .table tbody tr:hover {
          background-color: #f8f9fa;
        }

        @media (max-width: 768px) {
          .hr-contacts-content {
            padding: 15px;
          }

          .table {
            font-size: 11px;
          }

          .table thead th,
          .table tbody td {
            padding: 8px 4px;
          }
        }
      `}</style>
    </div>
  )
}

export default HRContactsTab
